// Real-weather integration (WEATHER-SYSTEM.md). Open-Meteo, no API key, no
// auth — and a fallback chain that is first-class, not an afterthought, because
// the first run of the game is a run with no city picked yet, not an edge case.
//
// The forecast is fetched for a city, chosen from a preset list or typed in.
// It is never fetched for the device's position: this used to ask a child
// where they were on every boot and put the answer in a save that uploads
// whole, which the schema's own header says is never done
// (supabase/migrations/0001_init.sql:12-13). Coordinates stay inside the
// forecast request and never enter the game state.

import { bus } from '../core/events';
import { Rng } from '../core/rng';
import type { StormAlert, WeatherCondition, WeatherState } from '../core/types';

const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';
const GEOCODE_URL = 'https://geocoding-api.open-meteo.com/v1/search';
const CACHE_KEY = 'clayfarm_weather_cache';
const CACHE_MAX_AGE_MS = 6 * 3_600_000;
export const POLL_INTERVAL_MS = 30 * 60_000;

/** WMO weather code → game state (WEATHER-SYSTEM.md §2). */
export function conditionFromWmoCode(code: number): WeatherCondition {
  if (code <= 1) return 'CLEAR';
  if (code <= 3) return 'CLOUDY';
  if (code === 45 || code === 48) return 'FOG';
  if (code >= 95) return 'STORM';
  // Snow codes map to rain visuals for MVP — PR has no snow content.
  if (
    (code >= 51 && code <= 67) ||
    (code >= 71 && code <= 77) ||
    (code >= 80 && code <= 86)
  ) {
    return 'RAIN';
  }
  return 'CLOUDY';
}

/** Tropical-storm-force gusts inside 72h raise a watch; hurricane force a warning. */
export function stormAlertFor(maxGustMph: number, weatherCodes: number[]): StormAlert {
  if (maxGustMph >= 74) return 'warning';
  if (maxGustMph >= 58) return 'watch';
  if (weatherCodes.some((c) => c >= 95) && maxGustMph >= 39) return 'watch';
  return 'none';
}

export interface Coordinates {
  latitude: number;
  longitude: number;
  label: string;
}

/** PR municipios first — the launch market picks from this list. */
export const CITY_PRESETS: Coordinates[] = [
  { latitude: 18.4655, longitude: -66.1057, label: 'San Juan, PR' },
  { latitude: 18.2011, longitude: -67.1391, label: 'Mayagüez, PR' },
  { latitude: 18.0111, longitude: -66.6141, label: 'Ponce, PR' },
  { latitude: 18.3808, longitude: -65.9574, label: 'Carolina, PR' },
  { latitude: 18.2013, longitude: -66.0336, label: 'Caguas, PR' },
  { latitude: 18.3496, longitude: -67.2541, label: 'Aguadilla, PR' },
  { latitude: 18.4274, longitude: -66.6641, label: 'Arecibo, PR' },
  { latitude: 25.7617, longitude: -80.1918, label: 'Miami, FL' },
];

function hoursFromIso(iso: string | undefined): number {
  if (!iso) return 6;
  const date = new Date(iso);
  return date.getHours() + date.getMinutes() / 60;
}

interface OpenMeteoResponse {
  current?: {
    temperature_2m: number;
    relative_humidity_2m: number;
    weather_code: number;
    wind_speed_10m: number;
    wind_gusts_10m: number;
    is_day: number;
  };
  daily?: {
    sunrise: string[];
    sunset: string[];
    wind_gusts_10m_max: number[];
    weather_code: number[];
  };
}

export function parseForecast(
  data: OpenMeteoResponse,
  label: string,
): WeatherState {
  const current = data.current;
  const daily = data.daily;
  // A 3-day horizon is what the hurricane watch is defined against.
  const gusts = daily?.wind_gusts_10m_max?.slice(0, 3) ?? [];
  const codes = daily?.weather_code?.slice(0, 3) ?? [];
  const maxGust = gusts.length ? Math.max(...gusts) : (current?.wind_gusts_10m ?? 0);

  return {
    condition: conditionFromWmoCode(current?.weather_code ?? 0),
    tempF: current?.temperature_2m ?? 82,
    humidity: current?.relative_humidity_2m ?? 70,
    windMph: current?.wind_speed_10m ?? 5,
    gustMph: current?.wind_gusts_10m ?? 8,
    isDay: (current?.is_day ?? 1) === 1,
    sunriseHour: hoursFromIso(daily?.sunrise?.[0]),
    sunsetHour: hoursFromIso(daily?.sunset?.[0]),
    stormAlert: stormAlertFor(maxGust, codes),
    source: 'live',
    fetchedAt: Date.now(),
    // The city name and nothing finer. The forecast was fetched for these
    // coordinates, but they do not travel any further than this fetch: the
    // state is persisted and uploaded, and a precise fix on a child is exactly
    // what the schema says is never stored.
    locationLabel: label,
  };
}

export async function fetchWeather(coordinates: Coordinates): Promise<WeatherState> {
  const params = new URLSearchParams({
    latitude: String(coordinates.latitude),
    longitude: String(coordinates.longitude),
    current:
      'temperature_2m,relative_humidity_2m,precipitation,weather_code,wind_speed_10m,wind_gusts_10m,is_day',
    daily: 'sunrise,sunset,wind_gusts_10m_max,weather_code',
    // °F and mph are the PR convention; the UI converts to °C on request.
    temperature_unit: 'fahrenheit',
    wind_speed_unit: 'mph',
    timezone: 'auto',
    forecast_days: '3',
  });

  const response = await fetch(`${FORECAST_URL}?${params}`);
  if (!response.ok) throw new Error(`Open-Meteo returned ${response.status}`);
  return parseForecast((await response.json()) as OpenMeteoResponse, coordinates.label);
}

export async function geocodeCity(name: string): Promise<Coordinates | null> {
  const params = new URLSearchParams({ name, count: '1', format: 'json' });
  const response = await fetch(`${GEOCODE_URL}?${params}`);
  if (!response.ok) return null;
  const data = (await response.json()) as {
    results?: Array<{ latitude: number; longitude: number; name: string; country_code: string }>;
  };
  const hit = data.results?.[0];
  return hit
    ? { latitude: hit.latitude, longitude: hit.longitude, label: `${hit.name}, ${hit.country_code}` }
    : null;
}

/**
 * Device-local cache of a picked city → its coordinates, so a free-text city is
 * geocoded once per device rather than on every 30-minute poll. Deliberately
 * NOT in the save: coordinates in the save are coordinates in the cloud.
 */
const CITY_CACHE_KEY = 'clayfarm_weather_cities';

function readCityCache(): Record<string, Coordinates> {
  try {
    const raw = localStorage.getItem(CITY_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, Coordinates>) : {};
  } catch {
    return {};
  }
}

function writeCityCache(cache: Record<string, Coordinates>): void {
  try {
    localStorage.setItem(CITY_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Private browsing: the city is re-geocoded on the next poll.
  }
}

/**
 * Turns a saved city name back into coordinates, so a save file carries a
 * string a child cannot be located by. Presets resolve without a network call;
 * anything else is geocoded once and remembered on this device.
 */
export async function resolveSavedCity(label: string | null | undefined): Promise<Coordinates | null> {
  if (!label) return null;
  const preset = CITY_PRESETS.find((city) => city.label === label);
  if (preset) return preset;
  const cached = readCityCache()[label];
  if (cached) return cached;
  try {
    const hit = await geocodeCity(label);
    if (hit) writeCityCache({ ...readCityCache(), [label]: hit });
    return hit;
  } catch {
    return null;
  }
}

/**
 * Last resort: plausible weather from the date and latitude, seeded so it is
 * stable across reloads. PR-flavoured — warm, humid, afternoon showers.
 */
export function simulatedWeather(date = new Date(), latitude = 18.4): WeatherState {
  const dayOfYear = Math.floor(
    (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 86_400_000,
  );
  const rng = new Rng((date.getFullYear() * 1000 + dayOfYear) >>> 0);
  const hour = date.getHours();

  // Seasonal swing is mild in the tropics and stronger further from the equator.
  const seasonal = Math.cos(((dayOfYear - 200) / 365) * Math.PI * 2);
  const latitudeFactor = Math.abs(latitude) / 90;
  const baseTemp = 82 - seasonal * 25 * latitudeFactor;
  const diurnal = Math.sin(((hour - 9) / 24) * Math.PI * 2) * 6;

  // Afternoon showers are the PR norm rather than the exception.
  const showerChance = hour >= 13 && hour <= 18 ? 0.35 : 0.1;
  const condition: WeatherCondition = rng.chance(showerChance)
    ? 'RAIN'
    : rng.chance(0.4)
      ? 'CLOUDY'
      : 'CLEAR';

  const daylight = 12 + seasonal * 2 * latitudeFactor;
  const sunriseHour = 12 - daylight / 2;

  return {
    condition,
    tempF: Math.round((baseTemp + diurnal) * 10) / 10,
    humidity: rng.int(60, 90),
    windMph: rng.int(3, 14),
    gustMph: rng.int(6, 22),
    isDay: hour >= sunriseHour && hour <= sunriseHour + daylight,
    sunriseHour: Math.round(sunriseHour * 100) / 100,
    sunsetHour: Math.round((sunriseHour + daylight) * 100) / 100,
    stormAlert: 'none',
    source: 'simulated',
    fetchedAt: Date.now(),
    locationLabel: 'Simulated weather',
  };
}

function readCache(): WeatherState | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as WeatherState;
    if (Date.now() - cached.fetchedAt > CACHE_MAX_AGE_MS) return null;
    return { ...cached, source: 'cached' };
  } catch {
    return null;
  }
}

function writeCache(state: WeatherState): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing: caching is a nicety, not a requirement.
  }
}

export interface WeatherResolveOptions {
  /**
   * A city the player (or their grown-up) picked in Settings. There is no
   * other source of location: `WeatherService.start()` used to reach for
   * `navigator.geolocation` on every boot, which asked a child where they were
   * and wrote the answer into a save that uploads whole. The city picker below
   * replaces it and is the only way weather gets a fix.
   */
  savedCity?: Coordinates | null;
}

/**
 * The fallback chain (WEATHER-SYSTEM.md §1), minus the geolocation step it used
 * to lead with: picked city → cached (≤6h) → simulated. Always returns weather.
 */
export async function resolveWeather(
  options: WeatherResolveOptions = {},
): Promise<WeatherState> {
  if (options.savedCity) {
    try {
      const state = await fetchWeather(options.savedCity);
      writeCache(state);
      return state;
    } catch (err) {
      console.warn('[weather] live fetch failed, falling back', err);
    }
  }

  return readCache() ?? simulatedWeather();
}

/** Polls on an interval while the tab is active, emitting on real change. */
export class WeatherService {
  private timer: ReturnType<typeof setInterval> | null = null;
  private current: WeatherState | null = null;
  private city: Coordinates | null = null;
  /** The city as the save spells it, so a cold boot can find it again. */
  private savedCityLabel: string | null = null;

  setCity(city: Coordinates | null): void {
    this.city = city;
    this.savedCityLabel = city?.label ?? null;
  }

  /**
   * Seeds the service from the save at boot. A named city becomes coordinates
   * here, before the first fetch; nothing else does.
   */
  async useSavedCity(label: string | null | undefined): Promise<void> {
    this.savedCityLabel = label ?? null;
    this.city = await resolveSavedCity(label);
  }

  getCurrent(): WeatherState | null {
    return this.current;
  }

  async refresh(): Promise<WeatherState> {
    if (!this.city && this.savedCityLabel) {
      this.city = await resolveSavedCity(this.savedCityLabel);
    }
    const next = await resolveWeather({ savedCity: this.city });
    const previous = this.current;
    this.current = next;

    if (!previous || previous.condition !== next.condition) {
      bus.emit('WEATHER_CHANGED', { weather: next });
    }
    if (previous?.stormAlert !== next.stormAlert && next.stormAlert !== 'none') {
      bus.emit('STORM_WATCH', { alert: next.stormAlert, weather: next });
    }
    return next;
  }

  start(intervalMs = POLL_INTERVAL_MS): void {
    this.stop();
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), intervalMs);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** Dev dashboard weather forcing (DEV-DASHBOARD.md §2). */
  force(condition: WeatherCondition, tempF?: number): WeatherState {
    const base = this.current ?? simulatedWeather();
    const forced: WeatherState = {
      ...base,
      condition,
      tempF: tempF ?? base.tempF,
      source: 'forced',
      fetchedAt: Date.now(),
    };
    this.current = forced;
    bus.emit('WEATHER_CHANGED', { weather: forced });
    return forced;
  }

  forceStormAlert(alert: StormAlert): WeatherState {
    const base = this.current ?? simulatedWeather();
    const forced: WeatherState = { ...base, stormAlert: alert, source: 'forced' };
    this.current = forced;
    bus.emit('STORM_WATCH', { alert, weather: forced });
    return forced;
  }
}
